/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IAssessmentsConfig, IQueuesConfig, ITasksConfig } from 'app/core/models/essette-interfaces';
import { apiCall } from 'app/shared/lib/api/api';
import { TaskService } from 'app/core/services/task-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { I18N } from 'aurelia-i18n';
import { safe } from 'app/shared/lib/utilities';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class TasksGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.string({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public tasksConfig!: ITasksConfig;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public queuesConfig!: IQueuesConfig;

    @bindable.objectNull({ defaultBindingMode: bindingMode.oneTime })
    public assessmentsConfig!: IAssessmentsConfig;

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected tasks: Array<Core.Task> = [];

    // When minimal, hide the Add Tasks/Show Inactive buttons
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected minimal: boolean = false;

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;

    protected descriptionPreviewLength = 200;

    protected validate = validate
    protected isValid: boolean = false;

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;
    protected $tasksForm!: Element;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: TaskService,
        private readonly i18n: I18N,
        private readonly context: HouseholdCasePageContext,
        ...rest: any[]) {

        super(...rest);
        this.checkPermissions();
    }

    protected bind(): void {
        // Check that config property is bound
        if(!(this.householdCaseId && this.tasksConfig)) {
            this.logger.warn("Household Case ID and/or Task Configuration not found!");
        }

        this.service.configure(this.tasksConfig);

        this.reload();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$modal.close();

        if(detail.form && detail.form.reset) {
            detail.form.reset();
        }

        // reload grid
        this.reload();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    protected buildDelegatedToMessage(task: Core.Task): string | null {
        const delegated = safe(() => task.delegated);
        if(!(delegated && delegated.queue && delegated.queue.$display)) { return null; }

        const stamp = task.metadata && task.metadata.delegated;
        if(!stamp) { return null; }

        const label = this.i18n.tr(this.tasksConfig.labelKey);
        const queue = delegated.queue.$display;
        const queueUser = (delegated.to && delegated.to.$display) || null;
        const key = queueUser ? 'core:messages.task.delegated.user' : 'core:messages.task.delegated.queue';

        // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
        return this.i18n.tr(key, { what: label, queue: queue, who: queueUser, stamp: stamp });
    }

    protected buildCreatedByMessage(task: Core.Task): string | null {
        const stamp = task.metadata?.created;
        const label = this.i18n.tr(this.tasksConfig.labelKey);

        if(task.metadata?.triggeredBy?.description) {
            const key = 'core:messages.task.triggeredBy'
            const source = task.metadata.triggeredBy.description;
            // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
            return this.i18n.tr(key, { what: label, source: source, stamp: stamp });
        }
        else {
            const key = 'core:messages.task.created'
            // return this.i18n.tr(key, { queue: queue, who: queueUser, timestamp: stamp.timestamp, user: byUser });
            return this.i18n.tr(key, { what: label, stamp: stamp });
        }
    }

    public async reload(): Promise<void> {
        await this.loadTasks();
    }

    protected addTasks(): void {
        if(!this.householdCaseId) {
            throw new Error('Missing required property: householdCaseId');
        }
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) {
            this.$form.reset();
        }
        this.$modal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadTasks(): Promise<void> {
        if(!this.householdCaseId) return;

        const includes: string[] = [
            'definition.type',
            'delegated.queue',
            'delegated.to',
            'externalSystem',
            'case',
            'case.member',
            'metadata.created.byUser',
            'metadata.completed.byUser',
            'metadata.delegated.byUser',
            'metadata.dueDateTimeSet.byUser'
        ];

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            sorting: this.sorting,
            page: this.$pager?.page || 0,
            top: this.pageSize
        };

        try {
            this.state.beginLoad();

            const response = await this.service.loadTasksByTarget(this.householdCaseId, options, includes);
            this.tasks = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
